import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config/api';

const STATUS_CONFIG = {
    'Pending':     { bg: '#fff8e1', border: '#f59e0b', text: '#92400e', icon: 'bi-clock-history',      label: 'รอดำเนินการ' },
    'Sent to Qcm': { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', icon: 'bi-arrow-right-circle', label: 'ส่ง QCM แล้ว' },
    'At QCM':      { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', icon: 'bi-building',            label: 'ที่ QCM' },
    'Pass QC':     { bg: '#f0fdf4', border: '#22c55e', text: '#166534', icon: 'bi-check-circle-fill',   label: 'ผ่าน QC' },
    'Fail QC':     { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', icon: 'bi-x-circle-fill',       label: 'ไม่ผ่าน QC' },
    'Declined':    { bg: '#fdf4ff', border: '#a855f7', text: '#6b21a8', icon: 'bi-slash-circle',        label: 'ปฏิเสธ' },
    'Cancelled':   { bg: '#f1f5f9', border: '#64748b', text: '#334155', icon: 'bi-x-circle',             label: 'ยกเลิก' },
};

const getStatus = (status) => STATUS_CONFIG[status] || { bg: '#f9fafb', border: '#9ca3af', text: '#374151', icon: 'bi-circle', label: status || 'ไม่ระบุ' };

const formatDate = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
};

const FILTER_TABS = [
    { key: 'all',          label: 'ทั้งหมด',     icon: 'bi-list-ul' },
    { key: 'Pending',      label: 'รอดำเนินการ', icon: 'bi-clock-history' },
    { key: 'Sent to Qcm',  label: 'ส่ง QCM แล้ว', icon: 'bi-arrow-right-circle' },
    { key: 'Pass QC',      label: 'ผ่าน QC',     icon: 'bi-check-circle-fill' },
    { key: 'Fail QC',      label: 'ไม่ผ่าน QC',  icon: 'bi-x-circle-fill' },
];

const QcHistory = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    useEffect(() => { fetchHistory(); }, []);
    useEffect(() => { setCurrentPage(1); }, [searchTerm, filter, startDate, endDate]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const data = await fifoService.getQcHistory();
            setRows(data);
        } catch (error) {
            toast.error('ไม่สามารถโหลดข้อมูลรายงาน QC ได้');
        } finally {
            setLoading(false);
        }
    };

    const statusCounts = {
        all: rows.length,
        Pending: rows.filter(r => r.status === 'Pending').length,
        'Sent to Qcm': rows.filter(r => r.status === 'Sent to Qcm' || r.status === 'At QCM').length,
        'Pass QC': rows.filter(r => r.status === 'Pass QC').length,
        'Fail QC': rows.filter(r => r.status === 'Fail QC').length,
    };

    const filteredRows = rows.filter(r => {
        const matchesSearch = !searchTerm ||
            r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.document_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.sn_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filter === 'all' ||
            (filter === 'Sent to Qcm' ? (r.status === 'Sent to Qcm' || r.status === 'At QCM') : r.status === filter);

        const matchesDateRange =
            (!startDate || new Date(r.created_at) >= new Date(startDate)) &&
            (!endDate || new Date(r.created_at) <= new Date(endDate + 'T23:59:59'));

        return matchesSearch && matchesFilter && matchesDateRange;
    });

    const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedRows = filteredRows.slice(startIndex, startIndex + itemsPerPage);

    const handleDownloadPDF = (documentId) => {
        // Public endpoint — no token needed, same as AllRequests.js
        window.open(`${API_URL}/fifo/requests/${documentId}/pdf`, '_blank');
    };

    const handleExportExcel = () => {
        if (filteredRows.length === 0) { toast.warning('ไม่มีข้อมูลสำหรับ Export'); return; }
        const excelData = filteredRows.map(r => ({
            'เลขที่เอกสาร': r.document_id,
            'วันที่สร้าง': formatDate(r.created_at),
            'ลูกค้า': r.customer_name || '-',
            'รหัสสินค้า': r.product_id || '-',
            'รายละเอียด': r.description || '-',
            'Serial Number': r.sn_number || '-',
            'สถานะ': getStatus(r.status).label,
            'หมายเหตุ QC': r.QcmRemark || '-',
            'ผู้ตรวจสอบ': r.QcmName || '-',
            'วันที่ต้องการใช้': formatDate(r.wantDate),
            'ผู้แจ้งงาน': r.requesterName || '-',
            'แผนก': r.department || '-',
        }));
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'รายงาน QC');
        ws['!cols'] = [15, 15, 25, 18, 35, 25, 15, 30, 15, 15, 20, 20].map(wch => ({ wch }));
        const suffix = startDate || endDate ? `_${startDate || 'เริ่มต้น'}_ถึง_${endDate || 'ปัจจุบัน'}` : '';
        XLSX.writeFile(wb, `รายงาน_QC${suffix}_${new Date().toLocaleDateString('th-TH').replace(/\//g, '-')}.xlsx`);
        toast.success('Export Excel สำเร็จ!');
    };

    if (loading) return <PageLoader message="กำลังโหลดรายงาน QC..." />;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h4 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                        <i className="bi bi-clipboard-data-fill me-2 text-primary"></i>
                        รายงาน QC ทั้งหมด
                    </h4>
                    <p className="text-muted mb-0 small">ประวัติผลตรวจสอบทุกเครื่อง ทุก Serial Number ในระบบ</p>
                </div>
                <button className="btn btn-outline-success btn-sm" onClick={handleExportExcel} disabled={filteredRows.length === 0}>
                    <i className="bi bi-file-earmark-excel me-1"></i> Export Excel
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
                {FILTER_TABS.map(tab => {
                    const active = filter === tab.key;
                    const count = statusCounts[tab.key] ?? 0;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                        >
                            <i className={`bi ${tab.icon} me-1`}></i>
                            {tab.label}
                            <span className="ms-1 badge bg-light text-dark">{count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search + Date Range */}
            <div className="d-flex gap-2 mb-4 flex-wrap align-items-end">
                <div style={{ flex: '1', minWidth: '200px' }}>
                    <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="ค้นหาลูกค้า, Serial, เลขที่เอกสาร, รายละเอียด..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="btn btn-outline-secondary" onClick={() => setSearchTerm('')}>
                                <i className="bi bi-x"></i>
                            </button>
                        )}
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2 border rounded-2 bg-white px-2" style={{ height: '31px' }}>
                    <i className="bi bi-calendar3 text-muted small"></i>
                    <input
                        type="date"
                        className="form-control form-control-sm border-0 p-0 shadow-none"
                        style={{ width: '125px' }}
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                    <span className="text-muted small">ถึง</span>
                    <input
                        type="date"
                        className="form-control form-control-sm border-0 p-0 shadow-none"
                        style={{ width: '125px' }}
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
                {(searchTerm || startDate || endDate || filter !== 'all') && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); setFilter('all'); }}>
                        <i className="bi bi-arrow-clockwise me-1"></i>รีเซ็ต
                    </button>
                )}
                <span className="text-muted small ms-auto align-self-center">{filteredRows.length} รายการ</span>
            </div>

            <div className="theme-card">
                <div className="theme-card-body p-0">
                    {paginatedRows.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                            {searchTerm || filter !== 'all' || startDate || endDate ? 'ไม่พบรายการที่ค้นหา' : 'ยังไม่มีข้อมูล QC'}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table theme-table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>วันที่สร้าง</th>
                                        <th>เลขที่เอกสาร</th>
                                        <th>ลูกค้า</th>
                                        <th>สินค้า</th>
                                        <th>Serial Number</th>
                                        <th>สถานะ</th>
                                        <th>หมายเหตุ QC</th>
                                        <th>ผู้ตรวจสอบ</th>
                                        <th className="text-center">การดำเนินการ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRows.map((r, idx) => {
                                        const st = getStatus(r.status);
                                        return (
                                            <tr key={idx}>
                                                <td className="text-nowrap">{formatDate(r.created_at)}</td>
                                                <td>
                                                    <span
                                                        className="fw-medium text-primary text-decoration-underline"
                                                        role="button"
                                                        onClick={() => navigate(`/qc-queue/${encodeURIComponent(r.document_id)}`)}
                                                    >
                                                        {r.document_id}
                                                    </span>
                                                </td>
                                                <td>{r.customer_name || '-'}</td>
                                                <td>
                                                    <div className="text-truncate" style={{ maxWidth: '220px' }}>{r.description || '-'}</div>
                                                </td>
                                                <td className="fw-medium">{r.sn_number}</td>
                                                <td className="text-nowrap">
                                                    <span style={{
                                                        background: st.bg, border: `1px solid ${st.border}`, color: st.text,
                                                        borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: 600,
                                                    }}>
                                                        <i className={`bi ${st.icon} me-1`}></i>{st.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="text-truncate" style={{ maxWidth: '200px' }}>{r.QcmRemark || '-'}</div>
                                                </td>
                                                <td>{r.QcmName || '-'}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDownloadPDF(r.document_id)}
                                                        title="ดู PDF"
                                                    >
                                                        <i className="bi bi-file-pdf"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="text-muted small">
                        แสดง {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredRows.length)} จาก {filteredRows.length} รายการ
                    </span>
                    <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        {[...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                                return (
                                    <button key={p} className={`btn btn-sm ${currentPage === p ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setCurrentPage(p)}>
                                        {p}
                                    </button>
                                );
                            } else if (p === currentPage - 2 || p === currentPage + 2) {
                                return <span key={p} className="btn btn-sm disabled">…</span>;
                            }
                            return null;
                        })}
                        <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QcHistory;
